import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { getMarketplaceCurated } from '../../../../services';
import { validateApiResponse } from '../../../../shared/utils/apiResponse';
import { mapMarketplaceCardToUi } from '../../utils';

const EMPTY = { activeSummary: { connected_count: 0, top: [] }, featured: null, curated: { popular: [], newFromPartners: [] } };

/**
 * Curated marketplace payload (CT-4163 / CT-4164): active summary, featured card,
 * and the two curated grids. Mock-first via `marketplaceService` (CT-3904).
 */
export function useMarketplaceCurated() {
  const { t } = useTranslation('admin-studio');
  const [data, setData] = useState(EMPTY);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchIdRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const res = await getMarketplaceCurated();
      if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
      const body = validateApiResponse(res, t('common.requestFailed')).body ?? {};
      setData({
        activeSummary: body.active_summary ?? EMPTY.activeSummary,
        featured: body.featured ? mapMarketplaceCardToUi(body.featured) : null,
        curated: {
          popular: (body.curated?.popular ?? []).map(mapMarketplaceCardToUi),
          newFromPartners: (body.curated?.new_from_partners ?? []).map(mapMarketplaceCardToUi),
        },
      });
    } catch (err) {
      if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
      setError(err?.message ?? t('common.requestFailed'));
      setData(EMPTY);
    } finally {
      if (mountedRef.current && fetchId === fetchIdRef.current) setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  return useMemo(() => ({ ...data, isLoading, error, refetch: load }), [data, isLoading, error, load]);
}
